import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import { auth, onAuthStateChanged, signOut } from './firebase';

// Pages
import Home from './pages/Home';
import Functions from './pages/Functions';
import FunctionDetail from './pages/FunctionDetail';
import Shortcuts from './pages/Shortcuts';
import ShortcutDetail from './pages/ShortcutDetail';
import SkillTest from './pages/SkillTest';
import CareerInsights from './pages/CareerInsights';
import Contact from './pages/Contact';
import AuthModal from './components/AuthModal';
import AdminDashboard from './pages/AdminDashboard';
import InquiryModal from './components/InquiryModal';
import { applyPageSeo } from './seo';

function App() {
  const [route, setRoute] = useState('home');
  const [subRoute, setSubRoute] = useState('');
  const [authModal, setAuthModal] = useState({ open: false, tab: 'login' });
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const openAuthModal = (tab = 'login') => {
    setAuthModal({ open: true, tab });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      if (currentUser && currentUser.email === 'admin@mrexcelsrilanka.com') {
        setRoute('admin');
        window.location.hash = '#/admin';
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (err) {
      console.error('Sign out error', err);
    }
  };

  useEffect(() => {
    const parseHash = () => {
      const hash = window.location.hash || '#/';
      if (hash.startsWith('#/functions/')) {
        setRoute('functions-detail');
        setSubRoute(decodeURIComponent(hash.replace('#/functions/', '')));
      } else if (hash === '#/functions') {
        setRoute('functions');
        setSubRoute('');
      } else if (hash.startsWith('#/shortcuts/')) {
        setRoute('shortcuts-detail');
        setSubRoute(decodeURIComponent(hash.replace('#/shortcuts/', '')));
      } else if (hash === '#/shortcuts') {
        setRoute('shortcuts');
        setSubRoute('');
      } else if (hash === '#/skill-test') {
        setRoute('skill-test');
        setSubRoute('');
      } else if (hash === '#/training') {
        setRoute('training');
        setSubRoute('');
      } else if (hash === '#/contact') {
        setRoute('contact');
        setSubRoute('');
      } else if (hash === '#/admin') {
        setRoute('admin');
        setSubRoute('');
      } else {
        setRoute('home');
        setSubRoute('');
      }
    };

    window.addEventListener('hashchange', parseHash);
    parseHash();

    return () => window.removeEventListener('hashchange', parseHash);
  }, []);

  useEffect(() => {
    applyPageSeo(route, subRoute);
  }, [route, subRoute]);

  const navigate = (path) => {
    window.location.hash = `#/${path}`;
  };

  const renderPage = () => {
    const ENABLE_AUTH = false; // Set to true to re-enable authentication lock & protection
    const isVerifiedUser = !ENABLE_AUTH || (user && (user.emailVerified || user.email === 'admin@mrexcelsrilanka.com'));
    const isAdmin = user && user.email === 'admin@mrexcelsrilanka.com';
    const isProtectedRoute = ['functions', 'functions-detail', 'shortcuts', 'shortcuts-detail', 'skill-test', 'training'].includes(route);

    if (authLoading && (isProtectedRoute || route === 'admin')) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <span className="spinner" style={{
            width: '32px',
            height: '32px',
            border: '3px solid rgba(11, 143, 85, 0.2)',
            borderTopColor: '#0b8f55',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }}></span>
          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      );
    }

    // Guard the admin dashboard route
    if (route === 'admin' && !isAdmin) {
      setTimeout(() => {
        window.location.hash = '#/';
        openAuthModal('login');
      }, 0);
      return <Home navigate={navigate} user={user} onAuthClick={openAuthModal} />;
    }

    if (isProtectedRoute && !isVerifiedUser) {
      setTimeout(() => {
        window.location.hash = '#/';
        openAuthModal('login');
      }, 0);
      return <Home navigate={navigate} user={user} onAuthClick={openAuthModal} />;
    }

    switch (route) {
      case 'home':
        return <Home navigate={navigate} user={user} onAuthClick={openAuthModal} />;
      case 'functions':
        return <Functions navigate={navigate} />;
      case 'functions-detail':
        return <FunctionDetail slug={subRoute} navigate={navigate} />;
      case 'shortcuts':
        return <Shortcuts navigate={navigate} />;
      case 'shortcuts-detail':
        return <ShortcutDetail slug={subRoute} navigate={navigate} />;
      case 'skill-test':
        return <SkillTest navigate={navigate} />;
      case 'training':
        return <CareerInsights navigate={navigate} />;
      case 'contact':
        return <Contact navigate={navigate} />;
      case 'admin':
        return <AdminDashboard navigate={navigate} user={user} onSignOut={handleSignOut} />;
      default:
        return <Home navigate={navigate} user={user} onAuthClick={openAuthModal} />;
    }
  };

  const isAdminRoute = route === 'admin';

  return (
    <div className="App">
      {!isAdminRoute && (
        <Header 
          currentRoute={route} 
          navigate={navigate} 
          onAuthClick={openAuthModal} 
          user={user} 
          onSignOut={handleSignOut} 
          onInquiryClick={() => {
            navigate('');
            setInquiryModalOpen(true);
          }}
        />
      )}
      {renderPage()}
      {!isAdminRoute && (
        <Footer currentRoute={route} navigate={navigate} user={user} onAuthClick={openAuthModal} />
      )}
      
      {authModal.open && (
        <AuthModal
          tab={authModal.tab}
          onClose={() => setAuthModal({ open: false, tab: 'login' })}
        />
      )}

      {inquiryModalOpen && (
        <InquiryModal
          onClose={() => setInquiryModalOpen(false)}
        />
      )}
    </div>
  );
}

export default App;
